<template>
  <div class="personal-login-content">
    <!-- Login Method Tabs -->
    <div class="login-method-tabs">
      <span
        class="method-tab"
        :class="{ active: loginMethod === 'wechat' }"
        @click="loginMethod = 'wechat'"
      >
        微信扫码登录
      </span>
      <span class="method-divider">|</span>
      <span
        class="method-tab"
        :class="{ active: loginMethod === 'phone' }"
        @click="loginMethod = 'phone'"
      >
        手机号登录
      </span>
    </div>

    <!-- WeChat QR Code Login -->
    <div
      v-if="loginMethod === 'wechat'"
      class="wechat-login-wrapper"
    >
      <div
        v-if="qrCodeLoading"
        class="qr-code-loading"
      >
        <el-icon class="is-loading">
          <Loading />
        </el-icon>
        <div class="loading-text">
          二维码加载中...
        </div>
      </div>
      <div
        v-else-if="qrCodeError"
        class="qr-code-error"
      >
        <el-icon><WarningFilled /></el-icon>
        <div class="error-text">
          {{ qrCodeError }}
        </div>
        <el-button
          type="text"
          class="retry-btn"
          @click="handleRefreshQrCode"
        >
          重新加载
        </el-button>
      </div>
      <div
        v-else-if="qrCodeUrl"
        class="qr-code-image-wrapper"
      >
        <!-- 状态展示区域 -->
        
        <div class="qr-code-container">
          <img
            :src="qrCodeUrl"
            alt="微信登录二维码"
            class="qr-code-image"
            :class="{ 'is-blur': qrCodeStatus === 'expired' || qrCodeStatus === 'scanned' }"
          >
          <div
            v-if="qrCodeStatus === 'Success'"
            class="qr-code-overlay"
          >
            <el-icon class="status-icon success">
              <CircleCheckFilled />
            </el-icon>
            <p class="status-text">
              扫码登录成功
            </p>
            <p class="status-sub-text" />
          </div>
          <div
            v-else-if="qrCodeStatus === 'expired'"
            class="qr-code-overlay cursor-pointer"
            @click="handleRefreshQrCode"
          >
            <el-icon class="status-icon error">
              <Refresh />
            </el-icon>
            <p class="status-text">
              二维码已失效
            </p>
            <p class="status-sub-text">
              点击刷新
            </p>
          </div>
        </div>
      </div>
      <div
        v-else
        class="qr-code-placeholder"
      >
        <div
          v-if="!agreedToTerms"
          class="qr-placeholder-content"
        >
          <el-icon class="qr-placeholder-icon warning">
            <WarningFilled />
          </el-icon>
          <div class="qr-placeholder-text">
            请先勾选同意隐私协议和注册协议
          </div>
        </div>
        <div
          v-else
          class="qr-placeholder-content"
        >
          <el-icon class="qr-placeholder-icon">
            <Picture />
          </el-icon>
          <div class="qr-placeholder-text">
            登录二维码
          </div>
        </div>
      </div>
    </div>

    <!-- Phone Login -->
    <div
      v-if="loginMethod === 'phone'"
      class="phone-login-wrapper"
    >
      <el-form
        ref="phoneLoginForm"
        :model="phoneLoginForm"
        :rules="phoneLoginRules"
        class="phone-login-form"
      >
        <el-form-item prop="phonenumber">
          <PhoneInput
            v-model="phoneLoginForm.phonenumber"
            placeholder="请输入手机号"
            @keyup.enter="handleLogin"
          />
        </el-form-item>
        <el-form-item prop="smsCode">
          <el-input
            v-model="phoneLoginForm.smsCode"
            placeholder="请输入验证码"
            clearable
            maxlength="6"
            @keyup.enter="handleLogin"
          >
            <template #append>
              <el-button
                :disabled="smsCodeCountdown > 0"
                :loading="smsCodeLoading"
                @click="handleGetSmsCode"
              >
                {{ smsCodeCountdown > 0 ? `${smsCodeCountdown}秒后重试` : '获取验证码' }}
              </el-button>
            </template>
          </el-input>
        </el-form-item>
      </el-form>
      <div class="phone-login-options">
        <a
          href="#"
          class="no-code-link"
          @click.prevent="handleNoCodeClick"
        >收不到验证码?</a>
      </div>
    </div>

    <!-- Agreement Checkbox -->
    <div class="agreement-section">
      <el-checkbox v-model="agreedToTerms">
        <span class="agreement-text">
          勾选即为同意
          <a
            href="#"
            class="agreement-link"
            @click.prevent="$emit('privacy-click')"
          >《隐私协议》</a>
          和
          <a
            href="#"
            class="agreement-link"
            @click.prevent="$emit('register-click')"
          >《注册协议》</a>
        </span>
      </el-checkbox>
    </div>

    <!-- Login Button (不显示在扫码登录) -->
    <div
      v-if="loginMethod !== 'wechat'"
      class="login-button-wrapper"
    >
      <el-button
        type="primary"
        class="login-btn"
        :disabled="!agreedToTerms"
        :loading="loginLoading"
        @click="handleLogin"
      >
        登录
      </el-button>
    </div>

    <!-- Phone Verification Dialog (强制绑定手机号) -->
    <el-dialog
      v-model="bindingMobilePhoneNumberDialogVisible"
      width="450px"
      title="绑定手机号"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
      custom-class="phone-verify-dialog"
      :append-to-body="true"
    >
      <div class="phone-verify-body">
        <!-- 表单区域 -->
        <el-form
          ref="phoneVerifyForm"
          :model="phoneVerifyForm"
          :rules="phoneVerifyRules"
          label-width="0"
          class="phone-verify-form"
        >
          <el-form-item prop="phonenumber">
            <PhoneInput
              v-model="phoneVerifyForm.phonenumber"
              placeholder="请输入手机号"
              class="phone-verify-input"
            >
              <template #append>
                <el-button
                  class="phone-verify-code-btn"
                  :disabled="verifySmsCodeCountdown > 0"
                  :loading="verifySmsCodeLoading"
                  @click="getBindingPhoneSmsCode"
                >
                  {{ verifySmsCodeCountdown > 0 ? `${verifySmsCodeCountdown}秒后重试` : '获取验证码' }}
                </el-button>
              </template>
            </PhoneInput>
          </el-form-item>
          <el-form-item prop="smsCode">
            <el-input
              v-model="phoneVerifyForm.smsCode"
              placeholder="请输入验证码"
              clearable
              maxlength="6"
              class="phone-verify-input"
            />
          </el-form-item>
        </el-form>

        <!-- 底部操作区 -->
        <div class="phone-verify-footer">
          <div class="phone-verify-help">
            <a
              href="#"
              class="no-code-link"
              @click.prevent="handleNoCodeClick"
            >
              收不到验证码？
            </a>
          </div>
        </div>

        <!-- 登录按钮 -->
        <div class="phone-verify-button-wrapper">
          <el-button
            type="primary"
            class="phone-verify-login-btn"
            :loading="verifyLoading"
            @click="bindingMobilePhoneNumberFunction"
          >
            绑定
          </el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'
import { Loading, WarningFilled, Picture, CircleCheckFilled, Refresh } from '@element-plus/icons-vue'
import { getQrCode, checkWechatQrCodeStatusLogin, personalSmsLogin, bindPhone, getPersonalSmsLoginCaptcha } from '@/api/login'
import { getSmsCaptcha } from '@/api/utils/captcha'
import PhoneInput from '@/components/PhoneInput/index.vue'
import { GLOBAL_PHONE_PATTERN } from '@/utils/idValidator'
import { showEnhancedAlertDialog } from '@/utils/messageUtils'

// 常量配置
const COUNTDOWN_TIME = 60 // 验证码倒计时时间（秒）
const POLLING_INTERVAL = 2000 // 轮询间隔（毫秒）
const RELOAD_DELAY = 1000 // 页面刷新延迟（毫秒）
const SUCCESS_SHOW_DELAY = 2000 // 成功状态显示延迟（毫秒）

export default {
  name: 'PersonalLogin',
  components: {
    Loading,
    WarningFilled,
    Picture,
    CircleCheckFilled,
    Refresh,
    PhoneInput
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  emits: ['login-success', 'privacy-click', 'register-click'],
  data() {
    // 手机号验证规则
    const validatePhone = (rule, value, callback) => {
      if (!value) {
        callback(new Error('请输入手机号'))
      } else if (!GLOBAL_PHONE_PATTERN.test(value)) {
        callback(new Error('请输入正确的手机号'))
      } else {
        callback()
      }
    }

    return {
      loginMethod: 'wechat', // 'wechat' or 'phone'
      agreedToTerms: false,
      loginLoading: false,

      // QR Code related
      qrCodeUrl: '',
      qrCodeTicket: '',
      qrCodeLoading: false,
      qrCodeError: '',
      qrCodeStatus: 'waiting', // 'waiting', 'expired', 'success'
      pollingTimer: null, // 轮询定时器
      isPolling: false, // 轮询状态标识

      // Phone Login Form
      phoneLoginForm: {
        phonenumber: '',
        smsCode: ''
      },
      phoneLoginRules: {
        phonenumber: [
          { required: true, validator: validatePhone, trigger: 'blur' }
        ],
        smsCode: [
          { required: true, message: '请输入验证码', trigger: 'blur' },
          { len: 6, message: '验证码为6位数字', trigger: 'blur' }
        ]
      },
      smsCodeLoading: false,
      smsCodeCountdown: 0,
      smsCodeTimer: null,

      // Phone Verification Dialog (强制绑定手机号)
      bindingMobilePhoneNumberDialogVisible: false,
      phoneVerifyForm: {
        phonenumber: '',
        smsCode: ''
      },
      phoneVerifyRules: {
        phonenumber: [
          { required: true, validator: validatePhone, trigger: 'blur' }
        ],
        smsCode: [
          { required: true, message: '请输入验证码', trigger: 'blur' },
          { len: 6, message: '验证码为6位数字', trigger: 'blur' }
        ]
      },
      verifySmsCodeLoading: false,
      verifySmsCodeCountdown: 0,
      verifySmsCodeTimer: null,
      verifyLoading: false
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.resetForm()
        if (this.loginMethod === 'wechat' && this.agreedToTerms) {
          this.fetchQrCode()
        }
      } else {
        this.clearTimers()
      }
    },
    loginMethod(newMethod) {
      if (newMethod === 'wechat' && this.visible && this.agreedToTerms) {
        this.fetchQrCode()
      } else {
        this.stopPolling()
        this.clearTimers()
      }
    },
    agreedToTerms(newVal) {
      if (this.loginMethod === 'wechat' && this.visible) {
        if (newVal) {
          // 勾选协议时生成二维码
          this.fetchQrCode()
        } else {
          // 取消勾选协议时停止轮询并清除二维码
          this.stopPolling()
          this.qrCodeUrl = ''
          this.qrCodeTicket = ''
          this.qrCodeError = ''
          this.qrCodeStatus = 'waiting'
        }
      }
    }
  },
  mounted() {
    // 组件挂载时，如果visible为true、是微信登录方式且已勾选协议，加载二维码
    if (this.visible && this.loginMethod === 'wechat' && this.agreedToTerms) {
      this.fetchQrCode()
    }
  },
  beforeUnmount() {
    this.clearTimers()
  },
  methods: {
    // ==================== 工具方法 ====================
    /**
     * 清除所有定时器
     */
    clearTimers() {
      if (this.smsCodeTimer) {
        clearInterval(this.smsCodeTimer)
        this.smsCodeTimer = null
      }
      if (this.verifySmsCodeTimer) {
        clearInterval(this.verifySmsCodeTimer)
        this.verifySmsCodeTimer = null
      }
      if (this.pollingTimer) {
        clearTimeout(this.pollingTimer)
        this.pollingTimer = null
      }
    },
    // ==================== 二维码登录相关 ====================
    /**
     * 处理刷新二维码（带协议检查）
     */
    handleRefreshQrCode() {
      if (!this.agreedToTerms) {
        ElMessage.warning('请先勾选同意隐私协议和注册协议')
        return
      }
      this.fetchQrCode()
    },
    /**
     * 获取二维码
     */
    async fetchQrCode() {
      // 如果未勾选协议，不生成二维码
      if (!this.agreedToTerms) {
        return
      }

      this.qrCodeLoading = true
      this.qrCodeError = ''
      this.qrCodeUrl = ''
      this.qrCodeTicket = ''
      this.qrCodeStatus = 'waiting'
      this.clearTimers()

      try {
        const response = await getQrCode()
        const { sceneId, qrCodeUrl } = response.data
        this.qrCodeUrl = qrCodeUrl
        this.qrCodeTicket = sceneId

        if (qrCodeUrl && sceneId) {
          this.startPolling()
        } else {
          this.qrCodeError = '获取二维码失败，请重试'
        }
      } catch (error) {
        this.qrCodeError = '获取二维码失败，请重试'
        console.error('获取二维码失败:', error)
      } finally {
        this.qrCodeLoading = false
      }
    },
    /**
     * 启动轮询检查扫码状态
     * 先停止旧轮询（如从手机号登录切回微信时 isPolling 可能仍为 true），再启动新轮询
     */
    startPolling() {
      this.stopPolling()
      this.isPolling = true
      this.doPolling()
    },
    /**
     * 执行轮询（递归调用）
     */
    async doPolling() {
      if (!this.isPolling || !this.qrCodeTicket) {
        this.isPolling = false
        return
      }

      try {
        await this.checkWechatQrCodeStatus()
      } catch (error) {
        console.error('轮询检查扫码状态失败:', error)
      }

      // 如果仍在轮询中且没有定时器，则设置下一次轮询
      if (this.isPolling && !this.pollingTimer) {
        this.pollingTimer = setTimeout(() => {
          this.pollingTimer = null
          this.doPolling()
        }, POLLING_INTERVAL)
      }
    },
    /**
     * 停止轮询
     */
    stopPolling() {
      this.isPolling = false
      if (this.pollingTimer) {
        clearTimeout(this.pollingTimer)
        this.pollingTimer = null
      }
    },
    /**
     * 检查扫码状态
     */
    async checkWechatQrCodeStatus() {
      if (!this.qrCodeTicket) {
        this.stopPolling()
        return
      }

      const response = await checkWechatQrCodeStatusLogin(this.qrCodeTicket)
      const { status, token } = response.data || {}

      if (status === '0') {
        this.qrCodeStatus = 'waiting'
      } else if (status === 'Success') {
        this.stopPolling()
        this.qrCodeStatus = 'Success'
        setTimeout(() => this.handleLoginSuccess(token, response.data), SUCCESS_SHOW_DELAY)
      } else if (status === '3') {
        this.stopPolling()
        this.qrCodeStatus = 'expired'
      }
    },
    /**
     * 处理二维码登录成功
     * @param {string} token
     * @param {Object} [loginData] - 登录返回的 LoginTokenVo
     */
    async handleLoginSuccess(token, loginData) {
      if (token) {
        this.$auth.setToken(token)
      }
      await this.afterTokenLogin(loginData)
    },
    // ==================== 手机号登录相关 ====================
    /**
     * 获取登录验证码
     */
    async handleGetSmsCode() {
      await this.$nextTick()
      await this.requestPersonalLoginSmsCode()
    },
    /**
     * 手机号登录
     */
    async handleLogin() {
      if (!this.agreedToTerms) {
        ElMessage.warning('请先同意隐私协议和注册协议')
        return
      }

      await this.$refs.phoneLoginForm.validate()

      this.loginLoading = true
      try {
        const response = await personalSmsLogin(this.phoneLoginForm.phonenumber, this.phoneLoginForm.smsCode)
        this.$auth.setToken(response.data.token)
        await this.afterTokenLogin(response.data)
      } catch (error) {
        console.error('登录失败:', error)
      } finally {
        this.loginLoading = false
      }
    },
    /**
     * Token 登录后：个人登录直接进入，不弹身份选择（切换身份时才展示全部）
     * @param {Object} [loginData]
     */
    async afterTokenLogin(loginData = {}) {
      const canSwitch = loginData.canSwitchIdentity !== undefined
        ? !!loginData.canSwitchIdentity
        : true
      this.$auth.setCanSwitchIdentity(canSwitch)
      await this.handleLoginSuccessFlow()
    },
    // ==================== 绑定手机号相关 ====================
    /**
     * 获取绑定手机号验证码
     */
    async getBindingPhoneSmsCode() {
      await this.requestSmsCode('verify')
    },
    /**
     * 绑定手机号
     */
    async bindingMobilePhoneNumberFunction() {
      await this.$refs.phoneVerifyForm.validate()

      this.verifyLoading = true
      try {
        await bindPhone(this.phoneVerifyForm.phonenumber, this.phoneVerifyForm.smsCode)
        ElMessage.success('手机号绑定成功')
        this.bindingMobilePhoneNumberDialogVisible = false
        
        await this.$auth.refreshMainBody()

        this.$emit('login-success')
        setTimeout(() => window.location.reload(), RELOAD_DELAY)
      } catch (error) {
        console.error('绑定手机号失败:', error)
      } finally {
        this.verifyLoading = false
      }
    },
    /**
     * 关闭绑定手机号弹窗
     */
    handleBindingPhoneClose() {
      this.bindingMobilePhoneNumberDialogVisible = false
    },
    /**
     * 处理"收不到验证码"点击
     */
    handleNoCodeClick() {
      ElMessage.info('如未收到验证码，请检查手机号是否正确或稍后重试')
    },
    // ==================== 通用工具方法 ====================
    /**
     * 启动倒计时
     * @param {String} type - 'login' | 'verify'
     */
    startCountdown(type = 'login') {
      const timerKey = type === 'verify' ? 'verifySmsCodeTimer' : 'smsCodeTimer'
      const countdownKey = type === 'verify' ? 'verifySmsCodeCountdown' : 'smsCodeCountdown'
      
      if (this[timerKey]) {
        clearInterval(this[timerKey])
        this[timerKey] = null
      }
      
      this[countdownKey] = COUNTDOWN_TIME
      this[timerKey] = setInterval(() => {
        this[countdownKey]--
        if (this[countdownKey] <= 0) {
          clearInterval(this[timerKey])
          this[timerKey] = null
          this[countdownKey] = 0
        }
      }, 1000)
    },
    /**
     * 个人手机号登录 - 获取验证码（专用接口，校验是否已扫码注册）
     */
    async requestPersonalLoginSmsCode() {
      const formRef = this.$refs.phoneLoginForm
      if (!formRef) {
        ElMessage.error('表单引用不存在，请刷新页面重试')
        return
      }

      formRef.validateField('phonenumber', async (errorMessage) => {
        if (errorMessage && typeof errorMessage === 'string' && errorMessage.trim() !== '') {
          ElMessage.error(errorMessage)
          return
        }

        const phonenumber = (this.phoneLoginForm.phonenumber || '').trim()
        if (!phonenumber) {
          ElMessage.warning('请输入手机号')
          return
        }

        this.smsCodeLoading = true
        try {
          const response = await getPersonalSmsLoginCaptcha(phonenumber)
          if (response && response.code === 200) {
            ElMessage.success('验证码已发送')
            this.startCountdown('login')
            return
          }
          await showEnhancedAlertDialog(
            response?.msg,
            '提示'
          )
        } catch (error) {
          console.error('获取登录验证码失败:', error)
        } finally {
          this.smsCodeLoading = false
        }
      })
    },
    /**
     * 绑定手机号 - 获取验证码（通用短信接口）
     */
    async requestSmsCode(formType = 'verify') {
      const formRef = this.$refs.phoneVerifyForm
      const formData = this.phoneVerifyForm
      const loadingKey = 'verifySmsCodeLoading'

      if (!formRef) {
        ElMessage.error('表单引用不存在，请刷新页面重试')
        return
      }

      formRef.validateField('phonenumber', async (errorMessage) => {
        if (errorMessage && typeof errorMessage === 'string' && errorMessage.trim() !== '') {
          ElMessage.error(errorMessage)
          return
        }

        const phonenumber = (formData?.phonenumber || '').trim()
        if (!phonenumber) {
          ElMessage.warning('请输入手机号')
          return
        }

        this[loadingKey] = true
        try {
          const response = await getSmsCaptcha(phonenumber)
          if (response && response.code === 200) {
            ElMessage.success('验证码已发送')
            this.startCountdown(formType)
          }
        } catch (error) {
          console.error('获取验证码失败:', error)
        } finally {
          this[loadingKey] = false
        }
      })
    },
    /**
     * 处理登录成功后的逻辑
     */
    async handleLoginSuccessFlow() {
      
      // 先刷新并获取当前用户信息，判断登录主体类型
      let currentUserInfo = await this.$auth.refreshMainBody()
      
      const isPhoneVerified = currentUserInfo.mainBodyPhone?.trim()
      
      if (!isPhoneVerified) {
        ElMessage.warning('请先完成手机号绑定')
        this.bindingMobilePhoneNumberDialogVisible = true
        return
      }

      ElMessage.success('登录成功')
      this.$emit('login-success')
      setTimeout(() => window.location.reload(), RELOAD_DELAY)
      // 全局拦截器已处理错误响应，这里不需要额外处理
    },
    /**
     * 重置表单和状态
     */
    resetForm() {
      this.loginMethod = 'wechat'
      this.agreedToTerms = false
      this.phoneLoginForm = { phonenumber: '', smsCode: '' }
      this.phoneVerifyForm = { phonenumber: '', smsCode: '' }
      this.qrCodeUrl = ''
      this.qrCodeTicket = ''
      this.qrCodeError = ''
      this.qrCodeStatus = 'waiting'
      this.bindingMobilePhoneNumberDialogVisible = false
      this.$refs.phoneLoginForm?.clearValidate()
      this.$refs.phoneVerifyForm?.clearValidate()
      this.clearTimers()
    }
  }
}
</script>

<style lang="scss" scoped>
.personal-login-content {
  min-height: 300px;
  padding: 20px 0;

  @media (max-width: 768px) {
    min-height: auto;
    padding-bottom: 10px;
  }
}

.login-method-tabs {
  text-align: center;
  margin-bottom: 30px;
  font-size: 14px;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }

  .method-tab {
    color: #999;
    cursor: pointer;
    padding: 0 10px;
    transition: color 0.3s;

    &:hover {
      color: #206ef4;
    }

    &.active {
      color: #206ef4;
      font-weight: 600;
    }
  }

  .method-divider {
    color: #e0e0e0;
    margin: 0 5px;
  }
}

// WeChat Login
.wechat-login-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .qr-code-loading,
  .qr-code-error {
    width: 240px;
    height: 240px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    border: 2px solid #e8ecf0;
    border-radius: 16px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    padding: 12px;
    box-sizing: border-box;
    position: relative;
    overflow: hidden;

    @media (max-width: 768px) {
      width: 200px;
      height: 200px;
    }

    &::before {
      content: '';
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.3), transparent);
      animation: shimmer 2s infinite;
    }

    .el-icon {
      font-size: 48px;
      color: #0056b3;
      margin-bottom: 15px;
      position: relative;
      z-index: 1;
    }

    .loading-text,
    .error-text {
      font-size: 14px;
      color: #666;
      margin-bottom: 10px;
      position: relative;
      z-index: 1;
      font-weight: 500;
    }

    .retry-btn {
      color: #0056b3;
      font-size: 14px;
      margin-top: 10px;
      position: relative;
      z-index: 1;
      font-weight: 500;
      transition: all 0.3s;

      &:hover {
        color: #004494;
        transform: translateY(-1px);
      }
    }
  }

    .qr-code-image-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;

    .qr-code-status-bar {
      width: 100%;
      max-width: 240px;
      margin-bottom: 12px;
      padding: 8px 12px;
      background-color: #f5f7fa;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 36px;
      box-sizing: border-box;

      .status-text {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 14px;
        font-weight: 500;

        .el-icon {
          font-size: 16px;
        }

        &.success {
          color: #67c23a;

          .el-icon {
            color: #67c23a;
          }
        }

        &.expired {
          color: #e6a23c;

          .el-icon {
            color: #e6a23c;
          }

          .refresh-btn {
            margin-left: 8px;
            padding: 0;
            color: #206ef4;
            font-size: 14px;
            font-weight: 500;
            transition: all 0.3s;

            &:hover {
              color: #004494;
              text-decoration: underline;
            }
          }
        }
      }
    }

    .qr-code-container {
      position: relative;
      width: 240px;
      height: 240px;
      
      @media (max-width: 768px) {
        width: 200px;
        height: 200px;
      }
      
      .qr-code-image {
        width: 100%;
        height: 100%;
        border: 2px solid #e8ecf0;
        border-radius: 16px;
        background-color: #ffffff;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
        padding: 12px;
        box-sizing: border-box;
        transition: all 0.3s ease;
        position: relative;
        overflow: hidden;

        &.is-blur {
          filter: blur(4px);
        }

        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          border-radius: 14px;
          padding: 2px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          mask-composite: exclude;
          opacity: 0;
          transition: opacity 0.3s;
        }

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);

          &::before {
            opacity: 1;
          }
        }
      }

      .qr-code-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(255, 255, 255, 0.9);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        z-index: 10;
        border-radius: 16px;
        transition: all 0.3s;
        
        &.cursor-pointer {
          cursor: pointer;
          &:hover {
            background-color: rgba(255, 255, 255, 0.95);
          }
        }

        .status-icon {
          font-size: 48px;
          margin-bottom: 12px;
          
          &.success {
            color: #67c23a;
          }
          &.error {
            color: #303133;
          }
        }
        
        .status-text {
            font-size: 16px;
            font-weight: 600;
            color: #303133;
            margin: 0 0 4px 0;
        }
        
        .status-sub-text {
            font-size: 14px;
            color: #909399;
            margin: 0;
        }
      }
    }
  }

  .qr-code-placeholder {
    width: 240px;
    height: 240px;
    background: linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%);
    border: 2px dashed #d0d0d0;
    border-radius: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    transition: all 0.3s;
    position: relative;
    overflow: hidden;

    @media (max-width: 768px) {
      width: 200px;
      height: 200px;
    }

    &::before {
      content: '';
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.5), transparent);
      animation: shimmer 3s infinite;
    }

    .qr-placeholder-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }

    .qr-placeholder-icon {
      font-size: 56px;
      color: #d0d0d0;
      margin-bottom: 16px;
      position: relative;
      z-index: 1;
      opacity: 0.6;

      &.warning {
        color: #e6a23c;
        opacity: 1;
      }
    }

    .qr-placeholder-text {
      font-size: 14px;
      color: #999;
      font-weight: 500;
      position: relative;
      z-index: 1;
      text-align: center;
      padding: 0 20px;
    }

    &:hover {
      border-color: #0056b3;
      background: linear-gradient(135deg, #f0f5ff 0%, #e6f0ff 100%);
    }
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%) translateY(-100%) rotate(45deg);
  }
  100% {
    transform: translateX(100%) translateY(100%) rotate(45deg);
  }
}

// Phone Login
.phone-login-wrapper {
  max-width: 360px;
  margin: 0 auto;

  @media (max-width: 768px) {
    max-width: 100%;
  }

  .phone-login-form {
    :deep(.el-form-item) {
      margin-bottom: 20px;

      @media (max-width: 768px) {
        margin-bottom: 15px;
      }

      .el-input__wrapper {
        border-radius: 6px;
        border: 1px solid #DCDFE6;
        background-color: #FFFFFF;
        box-shadow: none;

        &:hover {
          border-color: #DCDFE6;
        }

        &.is-focus {
          border-color: #206ef4;
          box-shadow: 0 0 0 1px #206ef4 inset;
        }
      }

      // 当使用 append 时，输入框只保留左侧圆角
      :deep(.el-input-group) {
        .el-input__wrapper {
          border-radius: 6px 0 0 6px;
        }
      }

      .el-input__inner {
        height: 45px;
        font-size: 14px;
        color: #333;
        
        &::placeholder {
          color: #C0C4CC;
          font-size: 14px;
        }
      }

      .el-input-group__append {
        background-color: #fff;
        border-color: #DCDFE6;
        padding: 0;
        border-left: 1px solid #DCDFE6;
        border-radius: 6px; // 完整圆角，与输入框圆角一致
        
        .el-button {
          height: 45px;
          padding: 0 20px;
          margin: 0;
          border: none;
          border-radius: 6px; // 完整圆角，与输入框圆角一致
          color: #206ef4;
          font-size: 14px;
          background: transparent;
          font-weight: 400;

          &:hover:not(:disabled) {
            color: #206ef4;
            background: transparent;
          }

          &:disabled {
            color: #C0C4CC;
            background: transparent;
          }
        }
      }
    }
  }

  .phone-login-options {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-top: 15px;
    font-size: 14px;

    .no-code-link {
      color: #206ef4;
      text-decoration: none;
      font-size: 14px;

      &:hover {
        text-decoration: underline;
      }
    }
  }
}

.agreement-section {
  margin-top: 25px;
  margin-bottom: 20px;
  text-align: center;

  @media (max-width: 768px) {
    margin-top: 15px;
    margin-bottom: 15px;
  }

  :deep(.el-checkbox) {
    .el-checkbox__label {
      font-size: 14px;
      color: #666;
      padding-left: 8px;
    }

    .el-checkbox__input.is-checked .el-checkbox__inner {
      background-color: #206ef4;
      border-color: #206ef4;
    }
  }

  .agreement-text {
    color: #666;
  }

  .agreement-link {
    color: #206ef4;
    text-decoration: none;
    margin: 0 2px;

    &:hover {
      text-decoration: underline;
    }
  }
}

.login-button-wrapper {
  margin-top: 20px;
  text-align: center;

  .login-btn {
    width: 100%;
    max-width: 360px;
    height: 45px;
    font-size: 16px;
    background-color: #0d63f3;
    border-color: #0d63f3;

    @media (max-width: 768px) {
      max-width: 100%;
      height: 40px;
      font-size: 14px;
    }

    &:hover:not(:disabled) {
      background-color: #0d63f3;
      border-color: #0d63f3;
      opacity: 0.9;
    }

    &:disabled {
      background-color: #c0c4cc;
      border-color: #c0c4cc;
    }
  }
}

.phone-verify-options {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 15px;
  margin-bottom: 20px;
  font-size: 14px;

  .no-code-link {
    color: #206ef4;
    text-decoration: none;
    font-size: 14px;

    &:hover {
      color: #206ef4;
      text-decoration: underline;
    }
  }
}

// Phone Verify Dialog Styles（绑定手机号弹窗，优化样式与设计图一致）
.phone-verify-body {
  padding: 24px;
}

.phone-verify-form {
  :deep(.el-form-item) {
    margin-bottom: 16px;

    // 普通 el-input 样式（验证码输入框）
    // :not(.area-select) 排除 PhoneInput 内部区号选择器
    :deep(.el-select):not(.area-select) .el-select__wrapper,
    .el-input__wrapper {
      border-radius: 4px;
      border: 1px solid #e0e0e0;
      background-color: #ffffff;
      box-shadow: none;
      padding: 0 12px;
      transition: border-color 0.3s;
      height: 40px;

      &:hover {
        border-color: #e0e0e0;
      }

      &.is-focus,
      &.is-focused {
        border-color: #206ef4;
        box-shadow: none;
      }
    }

    .el-input__inner {
      font-size: 14px;
      color: #333;

      &::placeholder {
        color: #c0c4cc;
        font-size: 14px;
      }
    }
  }

  // PhoneInput 专属覆盖：区号+手机号+验证码按钮一行
  .phone-verify-input {
    height: 40px !important;
    border-color: #e0e0e0 !important;
    border-radius: 4px;

    &.is-focused {
      border-color: #206ef4 !important;
      box-shadow: none !important;
    }

    :deep(.append-slot) {
      .phone-verify-code-btn {
        height: 100%;
        border: none;
        padding: 0 12px;
        color: #206ef4;
        background: transparent;
        font-size: 14px;
        border-radius: 0;
        white-space: nowrap;
        font-weight: 400;
        transition: color 0.3s;

        &:hover:not(:disabled) {
          background: #f5f7fa;
        }

        &:disabled {
          color: #c0c4cc;
          cursor: not-allowed;
        }
      }
    }
  }
}

.phone-verify-button-wrapper {
  margin-top: 24px;
  text-align: center;

  .phone-verify-login-btn {
    width: 100%;
    height: 44px;
    font-size: 16px;
    background-color: #206ef4;
    border-color: #206ef4;
    border-radius: 4px;
    color: #ffffff;
    font-weight: 500;
    transition: all 0.3s;

    &:hover:not(:disabled) {
      background-color: #206ef4;
      border-color: #206ef4;
      opacity: 0.9;
    }

    &:active:not(:disabled) {
      opacity: 0.8;
    }

    &:disabled {
      background-color: #c0c4cc;
      border-color: #c0c4cc;
      color: #ffffff;
      opacity: 1;
    }
  }
}

.phone-verify-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 16px;
  font-size: 14px;

  .phone-verify-help {
    .no-code-link {
      color: #c0c4cc;
      text-decoration: none;
      font-size: 14px;

      &:hover {
        color: #909399;
        text-decoration: none;
      }
    }
  }
}
</style>

<style lang="scss">
.phone-verify-dialog {
  z-index: 3000 !important;

  .el-dialog__header {
    margin: 0;
    padding: 20px 24px;
    background-color: #ffffff;
    border-bottom: none;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }

  .el-dialog__title {
    font-size: 16px;
    font-weight: 500;
    color: #333333;
    text-align: center;
  }

  .el-dialog__headerbtn {
    top: 20px;
    right: 24px;
  }

  .el-dialog__headerbtn .el-dialog__close {
    font-size: 18px;
    color: #909399;
    transition: color 0.2s;
  }

  .el-dialog__headerbtn .el-dialog__close:hover {
    color: #606266;
  }

  .el-dialog__body {
    padding: 0;
    background-color: #ffffff;
  }

  .el-dialog {
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    background-color: #ffffff;
  }

  .el-dialog__wrapper {
    background-color: rgba(0, 0, 0, 0.5);
  }
}
</style>
